import { a as __rest } from '../../tslib.es6.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import * as React from 'react';
import { useRef, useImperativeHandle } from 'react';
import { MessageInputButton } from '../MessageInputButton/MessageInputButton.esm.js';
import { useAutoResize } from './hooks/useAutoResize.esm.js';
import { useControlledValue } from './hooks/useControlledValue.esm.js';
import { useInputModality } from './hooks/useInputModality.esm.js';
import messageInputStyles from './MessageInput.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

const MessageInput = React.forwardRef((_a, ref) => {
    var { value, onChange, showPlaceholder = true, defaultValue, placeholder = "Placeholder", inverse = false, buttonVariant = "send", className, onSubmit, inputClassName, buttonClassName, rows = 1, "aria-label": ariaLabel = "Message input field" } = _a, props = __rest(_a, ["value", "onChange", "showPlaceholder", "defaultValue", "placeholder", "inverse", "buttonVariant", "className", "onSubmit", "inputClassName", "buttonClassName", "rows", "aria-label"]);
    const isKeyboard = useInputModality();
    const textareaRef = useRef(null);
    useImperativeHandle(ref, () => textareaRef.current);
    const { value: inputValue, isControlled, setInternalValue, } = useControlledValue(value, defaultValue);
    const adjustHeight = useAutoResize(textareaRef, inputValue);
    const { container, input, buttonWrapper } = messageInputStyles({
        inverse,
        isKeyboardFocus: isKeyboard,
    });
    const handleInputChange = (e) => {
        if (!isControlled) {
            setInternalValue(e.target.value);
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(e.target.value);
        adjustHeight();
    };
    const handleSubmit = () => {
        // For send button, only submit if there's text
        if (buttonVariant === "send" && !inputValue.trim()) {
            return;
        }
        // Call onSubmit for both send and stop
        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit();
        // Only clear text when send button is clicked
        if (buttonVariant === "send") {
            if (!isControlled) {
                setInternalValue("");
            }
            else {
                onChange === null || onChange === void 0 ? void 0 : onChange("");
            }
            adjustHeight();
        }
    };
    const handleKeyDown = (e) => {
        var _a;
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
        (_a = props.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(props, e);
    };
    const handleButtonClick = (e) => {
        var _a;
        e.preventDefault();
        handleSubmit();
        (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const isButtonDisabled = buttonVariant === "send" ? !inputValue.trim() : false;
    const shouldShowPlaceholder = showPlaceholder && !inputValue;
    return (jsxs("div", { className: cn(container(), className), children: [jsx("textarea", Object.assign({ ref: textareaRef, value: inputValue, onChange: handleInputChange, onKeyDown: handleKeyDown, placeholder: shouldShowPlaceholder ? placeholder : undefined, className: cn(input(), inputClassName), rows: rows, "aria-label": ariaLabel }, props)), jsx("div", { className: buttonWrapper(), children: jsx(MessageInputButton, { variant: buttonVariant, inverse: inverse, disabled: isButtonDisabled, onClick: handleButtonClick, className: buttonClassName }) })] }));
});
MessageInput.displayName = "MessageInput";

export { MessageInput };
